/**
 * commissionGoals.ts — the annual commission goal model (Sep 28, 2026).
 *
 * Greg: "I want to have a goal chart for each team member to track their
 * commissions for the year and where it is projecting them. projected based
 * on points with actual too. rolling 30's. set goal at $60k for year, rolling
 * 30 will be 20 apps/6 claims/2 builds; set goal at 90K, rolling 30 will be
 * at 30 apps/9 claims/3 builds. set goal at 120k … 40 apps/12 claims/4 builds."
 *
 * The three RM tiers are the same recipe scaled, so points convert to dollars
 * at ONE rate: 58 / 87 / 116 points per 30 days ↔ $60k / $90k / $120k, i.e.
 * every rolling-30 point is worth base.goal / (basePts × 365/30) ≈ $85.03 of
 * annual pace at the 10% RM base rate (≈ $1,034/yr per point in the window).
 *
 * Decisions (Greg, Sep 28, 2026):
 *   • points = app 1 · claim 3 · build 10; Signed IA / reviews / revenue are
 *     counts + weekly goals only, never points (mirror of the DB function
 *     crew_activity_points — change both together).
 *   • "build" = Chad approves and it lands on the calendar (the `build` kind).
 *   • The year is the CREW COMPETITION YEAR (first Friday of December →
 *     the next one), not the calendar year — same clock as crew_year_start().
 *   • The goal is picked at the Monday 1-on-1 (TL/ASM) and stored in
 *     crew_goals (scope personal · period year · metric dollars).
 *   • TL/ASM get separate rungs and are valued on own + downline points:
 *     own points at their own rate, downline points at the override rate
 *     (1% TL / 4% ASM ÷ the 10% base). They still see it as their goal.
 *   • Mid-year hires are pro-rated: the "where the goal says you'd be" marker
 *     and the on-pace line start at hire_date.
 *
 * Money in the chart comes from the SAME engine the Paid tab and My Business
 * band use (groupByPersonStored → myLines) — this module only slices it by
 * the crew year. Nothing here re-derives a commission.
 */
import { supabase } from "./supabase";
import type { TeamMember } from "./types";
import { anchorDay, moneyScopeIds, myLines } from "./businessSnapshot";
import type { PersonGroup } from "../components/commissions/CommissionBoard";

export type GoalRole = "restore_manager" | "team_lead" | "asm";
export const GOAL_ROLES = new Set<string>(["restore_manager", "team_lead", "asm"]);

export interface GoalTierConfig {
  /** Points per activity — mirror of crew_activity_points() in the DB. */
  points: { app: number; claim: number; build: number };
  /** The RM recipe that anchors $/point: goal at the base rate ↔ this 30-day mix. */
  base: { goal: number; apps: number; claims: number; builds: number };
  /** Goal choices (rungs) per role, ascending. */
  rungs: Record<GoalRole, number[]>;
  /** Override rates used to value downline points for TL / ASM pace. */
  override: { team_lead: number; asm: number };
  /** The RM base commission rate the recipe is written at. */
  rmBaseRate: number;
}

/** Identical to the seeded finance_settings.commission_goal_tiers row. */
export const DEFAULT_GOAL_TIERS: GoalTierConfig = {
  points: { app: 1, claim: 3, build: 10 },
  base: { goal: 60000, apps: 20, claims: 6, builds: 2 },
  rungs: {
    restore_manager: [60000, 90000, 120000],
    // ⚠ TL / ASM rungs are placeholders (Sep 28, 2026) — Greg confirms or
    // edits finance_settings.commission_goal_tiers (no deploy).
    team_lead: [150000, 200000, 250000],
    asm: [300000, 400000, 500000],
  },
  override: { team_lead: 0.01, asm: 0.04 },
  rmBaseRate: 0.1,
};

const num = (v: unknown, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

/** finance_settings.commission_goal_tiers merged over the defaults (best-effort). */
export async function fetchGoalTierConfig(): Promise<GoalTierConfig> {
  const d = DEFAULT_GOAL_TIERS;
  try {
    const { data, error } = await supabase
      .from("finance_settings")
      .select("value_jsonb")
      .eq("key", "commission_goal_tiers")
      .maybeSingle();
    if (error || !data?.value_jsonb || typeof data.value_jsonb !== "object") return d;
    const v = data.value_jsonb as Record<string, unknown>;
    const p = (v.points ?? {}) as Record<string, unknown>;
    const b = (v.base ?? {}) as Record<string, unknown>;
    const r = (v.rungs ?? {}) as Record<string, unknown>;
    const o = (v.override ?? {}) as Record<string, unknown>;
    const rungs = (role: GoalRole): number[] => {
      const arr = Array.isArray(r[role]) ? (r[role] as unknown[]).map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0) : [];
      return arr.length ? [...arr].sort((a, c) => a - c) : d.rungs[role];
    };
    return {
      points: { app: num(p.app, d.points.app), claim: num(p.claim, d.points.claim), build: num(p.build, d.points.build) },
      base: { goal: num(b.goal, d.base.goal), apps: num(b.apps, d.base.apps), claims: num(b.claims, d.base.claims), builds: num(b.builds, d.base.builds) },
      rungs: { restore_manager: rungs("restore_manager"), team_lead: rungs("team_lead"), asm: rungs("asm") },
      override: { team_lead: num(o.team_lead, d.override.team_lead), asm: num(o.asm, d.override.asm) },
      rmBaseRate: num(v.rmBaseRate, d.rmBaseRate),
    };
  } catch {
    return d;
  }
}

// ── Dates (all ISO YYYY-MM-DD, day math in UTC so DST can't shift a day) ──

const MS_DAY = 86400000;
const utcMs = (iso: string): number => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Whole days from a to b (b − a). */
export function daysBetween(aISO: string, bISO: string): number {
  return Math.round((utcMs(bISO) - utcMs(aISO)) / MS_DAY);
}
export function addDays(iso: string, n: number): string {
  return isoOf(utcMs(iso) + n * MS_DAY);
}

/** Mirror of crew_first_friday_of_december(y) in the DB. */
export function firstFridayOfDecember(y: number): string {
  const dec1 = Date.UTC(y, 11, 1);
  const dow = new Date(dec1).getUTCDay(); // 0 = Sunday … 5 = Friday
  return isoOf(dec1 + ((5 - dow + 7) % 7) * MS_DAY);
}

export interface CrewYear {
  /** First day of the competition year (inclusive). */
  start: string;
  /** First day of the NEXT competition year (exclusive) — the crowning date. */
  end: string;
  /** Length in days (52 or 53 weeks). */
  days: number;
}

/** Mirror of crew_year_start() / crew_year_end(): first Friday of December → the next one. */
export function crewYearFor(todayISO: string): CrewYear {
  const y = Number(todayISO.slice(0, 4));
  const thisDec = firstFridayOfDecember(y);
  const start = todayISO >= thisDec ? thisDec : firstFridayOfDecember(y - 1);
  const end = todayISO >= thisDec ? firstFridayOfDecember(y + 1) : thisDec;
  return { start, end, days: daysBetween(start, end) };
}

export function fmtDayShort(iso: string): string {
  return new Date(utcMs(iso)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// ── Activity (crew_daily_points) ─────────────────────────────────────────

export interface ActivityCounts { apps: number; claims: number; builds: number }
export interface DailyPointRow extends ActivityCounts { user_id: string; day: string }

export const ZERO_ACTIVITY: ActivityCounts = { apps: 0, claims: 0, builds: 0 };

/** Daily activity rows for a set of people over [fromISO, toISO], paged past the 1,000-row cap. */
export async function fetchDailyPoints(userIds: string[], fromISO: string, toISO: string): Promise<DailyPointRow[]> {
  if (userIds.length === 0) return [];
  const out: DailyPointRow[] = [];
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("crew_daily_points")
      .select("user_id, day, apps, claims, builds")
      .in("user_id", userIds)
      .gte("day", fromISO)
      .lte("day", toISO)
      .order("day", { ascending: true })
      .order("user_id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Array<{ user_id: string; day: string; apps: unknown; claims: unknown; builds: unknown }>;
    for (const r of rows) {
      out.push({ user_id: r.user_id, day: String(r.day).slice(0, 10), apps: Number(r.apps ?? 0), claims: Number(r.claims ?? 0), builds: Number(r.builds ?? 0) });
    }
    if (rows.length < PAGE) break;
    if (from > 20_000) break; // runaway guard
  }
  return out;
}

/** Sum of apps/claims/builds for `ids` over the `days` ending at `endISO` (inclusive). */
export function sumWindow(rows: DailyPointRow[], ids: Set<string>, endISO: string, days = 30): ActivityCounts {
  const from = addDays(endISO, -(days - 1));
  const s = { apps: 0, claims: 0, builds: 0 };
  for (const r of rows) {
    if (!ids.has(r.user_id) || r.day < from || r.day > endISO) continue;
    s.apps += r.apps; s.claims += r.claims; s.builds += r.builds;
  }
  return s;
}

export function pointsOf(c: ActivityCounts, cfg: GoalTierConfig = DEFAULT_GOAL_TIERS): number {
  return c.apps * cfg.points.app + c.claims * cfg.points.claim + c.builds * cfg.points.build;
}

/** Rolling-30 points sampled every `stepDays`, oldest → newest, ending today. */
export function rollingSeries(
  rows: DailyPointRow[], ids: Set<string>, endISO: string, cfg: GoalTierConfig, windows = 20, stepDays = 7,
): Array<ActivityCounts & { end: string; pts: number }> {
  const out: Array<ActivityCounts & { end: string; pts: number }> = [];
  for (let i = windows - 1; i >= 0; i--) {
    const end = addDays(endISO, -i * stepDays);
    const c = sumWindow(rows, ids, end);
    out.push({ ...c, end, pts: pointsOf(c, cfg) });
  }
  return out;
}

/** Everyone whose points feed this person's override lines (TL: their RMs; ASM: the whole downline). Excludes self. */
export function downlineIds(member: Pick<TeamMember, "id" | "role" | "team_lead_id" | "asm_id">, roster: TeamMember[]): string[] {
  return moneyScopeIds(member as TeamMember, roster).filter((id) => id !== member.id);
}

// ── Goals (crew_goals · personal · year · dollars) ───────────────────────

export async function fetchYearDollarGoal(userId: string, yearStart: string): Promise<number | null> {
  const { data, error } = await supabase
    .from("crew_goals")
    .select("target")
    .eq("scope", "personal")
    .eq("user_id", userId)
    .eq("period", "year")
    .eq("metric", "dollars")
    .eq("period_start", yearStart)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const t = data ? Number((data as { target: unknown }).target) : NaN;
  return Number.isFinite(t) && t > 0 ? t : null;
}

/** The year goal with who set it last and when (created_by / created_at are refreshed on every save). */
export interface YearGoalRow { target: number; setBy: string | null; setAt: string | null }

export async function fetchYearDollarGoalRow(userId: string, yearStart: string): Promise<YearGoalRow | null> {
  const { data, error } = await supabase
    .from("crew_goals")
    .select("target, created_by, created_at")
    .eq("scope", "personal")
    .eq("user_id", userId)
    .eq("period", "year")
    .eq("metric", "dollars")
    .eq("period_start", yearStart)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const row = data as { target: unknown; created_by: string | null; created_at: string | null } | null;
  const t = row ? Number(row.target) : NaN;
  return Number.isFinite(t) && t > 0 ? { target: t, setBy: row?.created_by ?? null, setAt: row?.created_at ?? null } : null;
}

/**
 * Upsert the year goal (one row per person per crew year); target ≤ 0 clears it.
 * Who may: a manager for anyone (goals_write_mgr), or the person for their own
 * year goal (goals_write_self_year, Oct 19, 2026 — set on My Pay, $1–$5M).
 * created_at is refreshed so it reads "last set at".
 */
export async function setYearDollarGoal(input: { userId: string; yearStart: string; target: number; createdBy: string }): Promise<void> {
  if (input.target <= 0) {
    const { error } = await supabase
      .from("crew_goals")
      .delete()
      .eq("scope", "personal")
      .eq("user_id", input.userId)
      .eq("period", "year")
      .eq("metric", "dollars")
      .eq("period_start", input.yearStart);
    if (error) throw new Error(error.message);
    return;
  }
  const { error } = await supabase.from("crew_goals").upsert(
    {
      scope: "personal",
      user_id: input.userId,
      period: "year",
      metric: "dollars",
      period_start: input.yearStart,
      target: input.target,
      created_by: input.createdBy,
      created_at: new Date().toISOString(),
    },
    { onConflict: "scope,user_id,period,period_start,metric" },
  );
  if (error) throw new Error(error.message);
}

// ── Money slice (from the real commission engine) ─────────────────────────

export interface CommissionYearSlice {
  start: string;
  /** My paid lines whose payout day falls inside the crew year, through today. */
  paidTotal: number;
  /** Same, bucketed by ET month "YYYY-MM". */
  paidByMonth: Record<string, number>;
  paidDeals: number;
}

/** Slice a groupByPerson(Stored) run to one person's paid lines inside the crew year. */
export function commissionYearSlice(groups: PersonGroup[], personId: string, yearStart: string, todayISO: string): CommissionYearSlice {
  const byMonth: Record<string, number> = {};
  let total = 0;
  const deals = new Set<string>();
  for (const it of myLines(groups, personId)) {
    const d = it.deal;
    if (!d.commission_paid) continue;
    const anchor = it.paidAt ?? d.commission_paid_at ?? d.commission_ready_date;
    if (!anchor) continue;
    const day = anchorDay(String(anchor));
    if (day < yearStart || day > todayISO) continue;
    total += it.amount;
    deals.add(d.id);
    const m = day.slice(0, 7);
    byMonth[m] = (byMonth[m] ?? 0) + it.amount;
  }
  return { start: yearStart, paidTotal: total, paidByMonth: byMonth, paidDeals: deals.size };
}

// ── The projection ────────────────────────────────────────────────────────

export const ANNUALIZE = 365 / 30;

/** Dollars of ANNUAL pace per rolling-30 point at the RM base rate ($85.03 × 12.17 ≈ $1,034). */
export function pointValueYear(cfg: GoalTierConfig = DEFAULT_GOAL_TIERS): number {
  const basePts = pointsOf({ apps: cfg.base.apps, claims: cfg.base.claims, builds: cfg.base.builds }, cfg);
  return basePts > 0 ? cfg.base.goal / basePts : 0;
}

export function rungsFor(role: string, cfg: GoalTierConfig = DEFAULT_GOAL_TIERS): number[] {
  return GOAL_ROLES.has(role) ? cfg.rungs[role as GoalRole] : cfg.rungs.restore_manager;
}

export interface Recipe extends ActivityCounts { pts: number }

/** The 30-day activity mix (in the base proportions) worth `pts` points. */
export function recipeForPoints(pts: number, cfg: GoalTierConfig = DEFAULT_GOAL_TIERS): Recipe {
  const basePts = pointsOf({ apps: cfg.base.apps, claims: cfg.base.claims, builds: cfg.base.builds }, cfg) || 1;
  const k = pts / basePts;
  const r1 = (n: number) => Math.round(n * 2) / 2; // halves — split credit is 0.5
  return { apps: r1(cfg.base.apps * k), claims: r1(cfg.base.claims * k), builds: r1(cfg.base.builds * k), pts: Math.round(pts * 10) / 10 };
}

export type Verdict = "on" | "close" | "behind";
export const VERDICT_LABEL: Record<Verdict, string> = { on: "On pace", close: "Close", behind: "Behind" };

export interface GoalInputs {
  role: string;
  /** Achieved RM rate (0.10 base / 0.12 achieved). null → base. */
  rate: number | null;
  own: ActivityCounts;
  /** Downline (TL: team RMs; ASM: everyone below). null for an RM. */
  downline: ActivityCounts | null;
  goal: number | null;
  paidYear: number;
  queued: number;
  year: CrewYear;
  todayISO: string;
  hireISO: string | null;
}

export interface GoalCalc {
  /** Annual pace from the rolling-30 window (own + override for TL/ASM). */
  pace: number;
  ownPace: number;
  overridePace: number;
  /** Own rolling-30 points. */
  ownPts: number;
  /** Own + downline rolling-30 points (== ownPts for an RM). */
  teamPts: number;
  /** The activity the recipe is measured against: own for RMs, own + downline for TL/ASM. */
  basis: "own" | "team";
  basisCounts: ActivityCounts;
  /** Blended $ of annual pace per point on the basis activity. */
  perPointYear: number;
  /** paid + queued + pace × days-left share. */
  est: number;
  /** Where the goal says you'd be today (pro-rated from hire date when it falls inside the year). */
  expected: number | null;
  /** Day the on-pace line starts: the crew year start, or the hire date. */
  paceStart: string;
  elapsedDays: number;
  daysLeft: number;
  verdict: Verdict | null;
  /** 30-day activity the goal needs on the basis (null without a goal). */
  recipe: Recipe | null;
  /** What's still missing in this window (0 when met). */
  gap: Recipe | null;
}

export function computeGoal(i: GoalInputs, cfg: GoalTierConfig = DEFAULT_GOAL_TIERS): GoalCalc {
  const pv = pointValueYear(cfg);
  const rate = i.rate && i.rate > 0 ? i.rate : cfg.rmBaseRate;
  const ownPerPt = pv * (rate / cfg.rmBaseRate);
  const ownPts = pointsOf(i.own, cfg);
  const isLead = (i.role === "team_lead" || i.role === "asm") && i.downline;
  const ovrRate = i.role === "asm" ? cfg.override.asm : i.role === "team_lead" ? cfg.override.team_lead : 0;
  const ovrPerPt = pv * (ovrRate / cfg.rmBaseRate);
  const downPts = isLead && i.downline ? pointsOf(i.downline, cfg) : 0;
  const ownPace = ownPts * ownPerPt;
  const overridePace = downPts * ovrPerPt;
  const pace = ownPace + overridePace;
  const teamPts = ownPts + downPts;
  const basis: "own" | "team" = isLead ? "team" : "own";
  const basisCounts: ActivityCounts = isLead && i.downline
    ? { apps: i.own.apps + i.downline.apps, claims: i.own.claims + i.downline.claims, builds: i.own.builds + i.downline.builds }
    : i.own;
  const perPointYear = isLead ? (teamPts > 0 ? pace / teamPts : ovrPerPt) : ownPerPt;

  const paceStart = i.hireISO && i.hireISO > i.year.start && i.hireISO <= i.todayISO ? i.hireISO : i.year.start;
  const elapsedDays = Math.max(1, daysBetween(paceStart, i.todayISO) + 1);
  const daysLeft = Math.max(0, daysBetween(i.todayISO, i.year.end) - 1);
  const est = i.paidYear + i.queued + pace * (daysLeft / 365);

  let expected: number | null = null, verdict: Verdict | null = null, recipe: Recipe | null = null, gap: Recipe | null = null;
  if (i.goal && i.goal > 0) {
    const spanDays = Math.max(1, daysBetween(paceStart, i.year.end));
    expected = i.goal * Math.min(1, elapsedDays / spanDays);
    verdict = pace >= i.goal ? "on" : pace >= i.goal * 0.85 ? "close" : "behind";
    const needPts = perPointYear > 0 ? i.goal / perPointYear : 0;
    recipe = recipeForPoints(needPts, cfg);
    const basisPts = pointsOf(basisCounts, cfg);
    gap = {
      apps: Math.max(0, recipe.apps - basisCounts.apps),
      claims: Math.max(0, recipe.claims - basisCounts.claims),
      builds: Math.max(0, recipe.builds - basisCounts.builds),
      pts: Math.max(0, Math.round((recipe.pts - basisPts) * 10) / 10),
    };
  }
  return { pace, ownPace, overridePace, ownPts, teamPts, basis, basisCounts, perPointYear, est, expected, paceStart, elapsedDays, daysLeft, verdict, recipe, gap };
}
